import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Check, Palette } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = 'fc_theme_v1';

// Apply theme CSS variables + body class globally
export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  root.style.setProperty('--theme-bg', theme.bg || '');
  root.style.setProperty('--theme-primary', theme.primary || '#D06892');
  root.style.setProperty('--theme-accent', theme.accent || '#FFC8DD');
  root.style.setProperty('--theme-card-bg', theme.card_bg || '#FFFFFF');
  root.style.setProperty('--theme-text', theme.text || '#2A1F3D');
  root.style.setProperty('--theme-font', theme.font || "'Fredoka', sans-serif");
  // body-level theme class for opt-in overrides
  document.body.className = (document.body.className || '')
    .split(' ')
    .filter((c) => !c.startsWith('theme-'))
    .concat([`theme-${theme.id}`])
    .join(' ').trim();
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(theme)); } catch { /* ignore */ }
}

// Load theme from localStorage on app start (instant), then sync from server
export function bootstrapTheme() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const theme = JSON.parse(cached);
      if (theme?.id) applyTheme(theme);
    }
  } catch { /* ignore */ }
}


export default function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const [themes, setThemes] = useState([]);
  const [current, setCurrent] = useState(null);
  const [mood, setMood] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cur] = await Promise.all([
        axios.get(`${API}/themes/list`, { withCredentials: true }),
        axios.get(`${API}/themes/current`, { withCredentials: true }),
      ]);
      const themeList = list.data?.themes || [];
      setThemes(themeList);
      const currentId = cur.data?.theme_id || 'kawaii';
      const currentTheme = themeList.find((t) => t.id === currentId);
      setCurrent(currentTheme);
      setMood(cur.data?.mood || '');
      if (currentTheme) applyTheme(currentTheme);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const choose = async (theme) => {
    setCurrent(theme);
    applyTheme(theme);
    try {
      await axios.post(`${API}/themes/current`, { theme_id: theme.id, mood: mood || null }, { withCredentials: true });
      toast.success(`${theme.emoji} ${theme.name}`);
    } catch { /* silent */ }
  };

  const saveMood = async () => {
    if (!current) return;
    try {
      await axios.post(`${API}/themes/current`, { theme_id: current.id, mood: mood || null }, { withCredentials: true });
      toast.success('Sparat 💗');
    } catch { /* silent */ }
  };

  return (
    <>
      {/* Floating toggle button — bottom-left */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-[220] w-12 h-12 rounded-full shadow-xl border-4 border-white flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        style={{
          background: current?.bg || 'linear-gradient(135deg, #FFC8DD, #C8B6FF)',
        }}
        data-testid="theme-switcher-toggle"
        title="Byt tema (Zinnia-stil)"
        aria-label="Byt tema"
      >
        <Palette className="w-5 h-5 text-white drop-shadow" />
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[230] flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="theme-switcher-modal"
        >
          <div
            className="neo-card bg-white p-4 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Zinnia mood</p>
                <h3 className="font-['Fredoka'] font-bold text-xl">Välj dagens vibe ✨</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1" data-testid="theme-switcher-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mood input */}
            <div className="neo-card p-2 bg-[#FFF8FB]">
              <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Hur känner du dig?</p>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  placeholder="t.ex. piggt, mysigt, drömskt..."
                  className="neo-input flex-1 text-sm"
                  data-testid="theme-mood-input"
                />
                <button
                  onClick={saveMood}
                  className="neo-btn px-3 py-1 bg-[#B9FBC0] text-xs font-bold"
                  data-testid="theme-mood-save"
                >
                  Spara
                </button>
              </div>
            </div>

            {/* Theme grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="theme-grid">
              {themes.map((t) => {
                const isCurrent = current?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => choose(t)}
                    className={`neo-card p-2 text-left transition-all hover:scale-[1.02] ${isCurrent ? 'ring-4 ring-[#D06892]' : ''}`}
                    style={{ background: t.bg, color: t.text }}
                    data-testid={`theme-option-${t.id}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="text-2xl">{t.emoji}</span>
                      {isCurrent && (
                        <span className="bg-white/90 rounded-full p-0.5">
                          <Check className="w-3 h-3 text-[#D06892]" />
                        </span>
                      )}
                    </div>
                    <p className="font-['Fredoka'] font-bold text-sm mt-1" style={{ color: t.text }}>{t.name}</p>
                    <p className="text-[10px] opacity-80" style={{ color: t.text }}>{t.vibe}</p>
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {(t.mood_tags || []).map((tag) => (
                        <span
                          key={tag}
                          className="text-[8px] px-1 py-0.5 rounded-full bg-white/40"
                          style={{ color: t.text }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
              {loading && <p className="col-span-full text-xs opacity-60 italic text-center">Laddar...</p>}
            </div>

            <p className="text-[10px] opacity-60 italic text-center">
              💡 Temat påverkar bakgrund + accentfärger globalt. Du kan byta hur ofta du vill.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
